import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { wxUtils } from "@features/wx/utils";
import { useJobCardTaskReport } from "../../../hooks";
import { Camera } from "lucide-react";
import { Checkbox } from "@shared/ui/checkbox";

const createPaginationConfig = (data: any[], page: number, perPage: number) => ({
  currentPage: page,
  totalPages: Math.ceil((data?.length || 0) / perPage),
  totalItems: data?.length || 0,
  itemsPerPage: perPage,
  startItem: (page - 1) * perPage + 1,
  endItem: Math.min(page * perPage, data?.length || 0),
});

const getPaginatedData = (data: any[], page: number, perPage: number) => {
  if (!data || data.length === 0) return [];
  const startIndex = (page - 1) * perPage;
  return data.slice(startIndex, startIndex + perPage);
};

export const TasksTab = ({
  jobCard,
  isReport,
  id,
  isHistory,
  isCompleted,
}: {
  jobCard: any;
  isReport?: boolean;
  id?: string;
  isHistory?: boolean;
  isCompleted?: boolean;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const { data: taskReportData } = useJobCardTaskReport(id, {
    enabled: !!isReport && !!id,
  });


  const transformedData = useMemo(
    () => jobCard && wxUtils.transformJobCardDetail(jobCard),
    [jobCard]
  );

  const reportTasks = useMemo(() => {
    return (
      taskReportData?.result?.task?.map((task, index) => ({
        taskId: index,
        taskName: task.name,
        tatMins: task.time,
        downtimeMins: parseInt(task.downtime) || 0,
        photoRequired: task.isPhotoRequired,
        isComplete: task.isComplete,
        status: task.status,
        taskFile: task.taskFile,
        photo: task.taskFile,
        executed: task.isComplete,
      })) || []
    );
  }, [taskReportData]);

  const tasksData = isReport ? reportTasks : transformedData?.tasksData || [];

  const tasksColumns = [
    {
      key: "taskName",
      header: "Task Name",
      render: (v: string) => (
        <span className="font-medium text-gray-900">{v}</span>
      ),
    },
    {
      key: "tatMins",
      header: "TAT (Mins)",
      render: (v: number) => <span>{v}</span>,
    },
    {
      key: "downtimeMins",
      header: "Downtime (Mins)",
      render: (v: number) => (
        <span className={v > 0 ? "text-orange-600" : "text-gray-700"}>{v}</span>
      ),
    },
    ...(isReport
      ? [
          {
            key: "photo",
            header: "Photo",
            render: (value, row) => (
              <div className="text-center">
                {row.taskFile ? (
                  <img
                    src={row.taskFile}
                    alt="Task photo"
                    className="w-8 h-8 object-cover rounded border-2 border-gray-300 mx-auto cursor-pointer"
                    onClick={() => window.open(row.taskFile, "_blank")}
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
                    <Camera className="h-3 w-3 text-gray-400" />
                  </div>
                )}
              </div>
            ),
          },
          {
            key: "executed",
            header: "Executed",
            render: (value, row) => (
              <div className="text-center">
                <Checkbox checked={row.isComplete} disabled />
              </div>
            ),
          },
          {
            key: "actions",
            header: "Actions",
            render: () => (
              <div className="text-center">
                <Checkbox defaultChecked={!isCompleted} disabled={isHistory} />
              </div>
            ),
          },
        ]
      : [
          {
            key: "photoRequired",
            header: "Photo Required",
            type: "badge" as const,
            badgeConfig: {
              getClassName: (v: string) =>
                v === "Yes"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800",
            },
          },
        ]),
  ];

  return (
    <GenericTable
      data={getPaginatedData(tasksData, currentPage, itemsPerPage)}
      columns={tasksColumns}
      emptyMessage={`No tasks found`}
      rowKey={"taskId"}
      pagination={createPaginationConfig(tasksData, currentPage, itemsPerPage)}
      onPageChange={setCurrentPage}
    />
  );
};


